import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { CourseContentModules, type CourseSection } from '@/components/courses/CourseContentModules';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';

interface CoursePreviewData {
  id: string;
  name: string;
  shortDescription?: string | null;
  fullDescription?: string | null;
  bannerImageUrl?: string | null;
  category?: { id: string } | null;
  sections?: CourseSection[];
}

export function CoursePreviewPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const canEditBuilder = hasPermission(PERMISSIONS.CONSTRUCTOR_EDITAR);

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['courses', id, 'preview'],
    queryFn: () => api<CoursePreviewData>(`/courses/${id}`),
    enabled: Boolean(id),
  });

  const itemCount =
    course?.sections?.reduce(
      (total, section) =>
        total +
        (section.items ?? []).filter((item) => item.itemType !== 'label' && item.isVisible !== false).length,
      0,
    ) ?? 0;

  if (!id) return null;

  return (
    <CourseHubShell
      courseId={id}
      courseName={course?.name ?? 'Curso'}
      categoryId={course?.category?.id}
      activeTab="curso"
      showProgress={itemCount > 0}
      progressPercent={0}
    >
      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : course ? (
        <>
          {course.bannerImageUrl && (
            <AuthenticatedImage
              src={course.bannerImageUrl}
              alt=""
              className="mb-8 h-48 w-full rounded-xl object-cover md:h-56"
            />
          )}
          <CourseContentModules courseId={id} sections={course.sections ?? []} adminContext canEditBuilder={canEditBuilder} />
        </>
      ) : null}
    </CourseHubShell>
  );
}
